import { CubeMapTexture } from './CubeMapTexture';
import { Context } from '../context/Context';
import { Camera } from '../camera';
import { SceneRenderer } from '../renderer';
import { Scene } from '../scene/Scene';

type SideBuffer = {
  buffer: WebGLBuffer;
  depthRenderbuffer?: WebGLRenderbuffer;
  side: string;
  glCubeMapSide: number;
};

export class RenderedCubeMapTexture extends CubeMapTexture {
  private sideBuffers!: SideBuffer[];
  private ctx!: Context;

  constructor(
    private switchCameraSide: (camera: Camera, side: string, glCubeMapSide: number) => Camera,
    private depthTexture?: boolean,
    private dimensions?: TextureDimensions,
  ) {
    super();

    if (!switchCameraSide) {
      throw new Error(`Must be able to switch camera to different sides`);
    }

    this.translucent = false;
  }

  public init(ctx: Context): void {
    this.ctx = ctx;

    if (!this.dimensions) {
      this.dimensions = ctx.viewPort;
    }

    if (this.depthTexture) {
      // Point light cube shadow maps store distance-to-light in a color texture,
      // not hardware depth. Hardware depth textures store gl_FragCoord.z which is
      // incompatible with the world-space distance comparison used for cube shadows.
      this.glTexture = this.createColorDepthTexture(this.dimensions);
      this.sideBuffers = this.createColorFramebuffers(this.dimensions);
    } else {
      this.glTexture = this.createColorTexture(this.dimensions);
      this.sideBuffers = this.createColorFramebuffers(this.dimensions);
    }
  }

  protected createColorDepthTexture(dimensions: TextureDimensions): WebGLTexture {
    const gl = this.ctx.gl;

    // define size and format of level 0
    const level = 0;
    const internalFormat = gl.RGBA32F;
    const border = 0;
    const format = gl.RGBA;
    const type = gl.FLOAT;
    const data = null;

    const colorTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, colorTexture);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_CUBE_MAP, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);

    for (let side of this.getSideMapping(this.ctx)) {
      gl.texImage2D(
        side.glCubeMapSide,
        level,
        internalFormat,
        dimensions.width,
        dimensions.height,
        border,
        format,
        type,
        data,
      );
    }

    return colorTexture;
  }

  protected createColorTexture(dimensions: TextureDimensions): WebGLTexture {
    const gl = this.ctx.gl;

    // define size and format of level 0
    const level = 0;
    const internalFormat = gl.RGBA;
    const border = 0;
    const format = gl.RGBA;
    const type = gl.UNSIGNED_BYTE;
    const data = null;

    const colorTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_CUBE_MAP, colorTexture);

    for (let side of this.getSideMapping(this.ctx)) {
      gl.texImage2D(
        side.glCubeMapSide,
        level,
        internalFormat,
        dimensions.width,
        dimensions.height,
        border,
        format,
        type,
        data,
      );
    }

    this.setMips(this.ctx, dimensions.width, dimensions.height);

    return colorTexture;
  }

  private createColorFramebuffers(dimensions: TextureDimensions): SideBuffer[] {
    const gl = this.ctx.gl;
    let buffers: SideBuffer[] = [];
    for (let side of this.getSideMapping(this.ctx)) {
      // Create and bind the framebuffer
      let fb = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fb);

      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        side.glCubeMapSide,
        this.glTexture!,
        0,
      );

      // create a depth renderbuffer
      const depthBuffer = gl.createRenderbuffer();
      gl.bindRenderbuffer(gl.RENDERBUFFER, depthBuffer);

      // make a depth buffer and the same size as the targetTexture
      const depthFormat = gl.DEPTH_COMPONENT24;
      gl.renderbufferStorage(gl.RENDERBUFFER, depthFormat, dimensions.width, dimensions.height);
      gl.framebufferRenderbuffer(gl.FRAMEBUFFER, gl.DEPTH_ATTACHMENT, gl.RENDERBUFFER, depthBuffer);

      if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
        throw new Error('this combination of attachments does not work');
      }

      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      buffers.push({
        buffer: fb,
        depthRenderbuffer: depthBuffer,
        side: side.reference,
        glCubeMapSide: side.glCubeMapSide,
      });
    }
    return buffers;
  }

  public renderTexture(renderer: SceneRenderer, scene: Scene, camera: Camera): void {
    for (let side of this.sideBuffers) {
      let newCamera = this.switchCameraSide(camera, side.side, side.glCubeMapSide);

      renderer.render(scene, newCamera, side.buffer);
    }
  }

  public destroy(): void {
    const gl = this.ctx.gl;
    if (this.glTexture) gl.deleteTexture(this.glTexture);
    if (this.sideBuffers) {
      for (const side of this.sideBuffers) {
        gl.deleteFramebuffer(side.buffer);
        if (side.depthRenderbuffer) gl.deleteRenderbuffer(side.depthRenderbuffer);
      }
    }
  }
}

type TextureDimensions = {
  width: number;
  height: number;
};
